/**
 * Saved ways of opening a shell: a host, a directory, and the first thing to run.
 *
 * TUIOS calls it a startup command. The value is the same here — "SRV4, tail the
 * node log" is a thing an operator does weekly and reassembles by hand every time
 * — but the delivery had to be different.
 *
 * **The command is typed, not launched.** `POST /sessions/shell` runs a fixed
 * argv on purpose: the handler says why, and SECURITY_MODEL.md §8.5 says it
 * again — the terminal package never builds a shell *string*, and a fixed argv
 * that happens to name a shell is only safe while nothing the caller sends
 * reaches it. Putting a template's command in that argv would quietly overturn a
 * written decision to save one round trip. So the session opens as any other
 * shell does, and the command arrives the way a person would deliver it: as
 * keystrokes, into a terminal, visible on screen as it lands.
 *
 * **Exactly once.** The entry is removed when it is taken, so a reconnect, a
 * remount or a second pane on the same session cannot run it again. A startup
 * command that repeats itself on every dropped socket is a command that runs
 * while nobody is watching.
 *
 * Stored locally, like the wall layouts and for the same reason: `/system/settings`
 * is a global key/value collection with no per-operator scope, so a template kept
 * there would be one operator's shortcut imposed on everyone.
 */

import { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'

export interface ShellTemplate {
  id: string
  name: string
  hostId: string
  workingDir?: string
  /** Typed into the shell once it is ready. Optional — a template can just be a
   *  host and a directory. */
  command?: string
}

const KEY = 'kimene.ssh.templates'

function valid(v: unknown): ShellTemplate[] | null {
  if (!Array.isArray(v)) return null
  const out: ShellTemplate[] = []
  for (const x of v) {
    if (!x || typeof x !== 'object') continue
    const t = x as ShellTemplate
    if (typeof t.id !== 'string' || typeof t.name !== 'string' || typeof t.hostId !== 'string')
      continue
    out.push({
      id: t.id,
      name: t.name,
      hostId: t.hostId,
      workingDir: typeof t.workingDir === 'string' ? t.workingDir : undefined,
      command: typeof t.command === 'string' ? t.command : undefined,
    })
  }
  return out
}

export function readTemplates(): ShellTemplate[] {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw == null) return []
    return valid(JSON.parse(raw)) ?? []
  } catch {
    // Written by a person or by another version of this code. An unreadable
    // preference is one to ignore, not one to report.
    return []
  }
}

export function writeTemplates(list: ShellTemplate[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list))
  } catch {
    // A full or blocked store must not take the screen down. The templates hold
    // for this tab; they just will not outlive it.
  }
}

/** Removes one by id. The store had a writer and a reader and no way back:
 *  every template ever saved was permanent, including the one saved by a typo. */
export function removeTemplate(list: readonly ShellTemplate[], id: string): ShellTemplate[] {
  return list.filter((x) => x.id !== id)
}

/** Adds or replaces by name, so saving twice under one name does not leave two
 *  identical rows with no way to tell which is current. */
export function upsertTemplate(list: readonly ShellTemplate[], t: ShellTemplate): ShellTemplate[] {
  const id = t.name.trim().toLocaleLowerCase('tr')
  if (!id) return [...list]
  return [...list.filter((x) => x.id !== id), { ...t, id, name: t.name.trim() }]
}

/* ------------------------------------------------------ delivering the command */

export interface Startup {
  /** Queue a command for a session that is about to open. */
  arm: (sessionId: string, command: string) => void
  /** Take it, once. Returns null if there was none, or it has already been taken. */
  take: (sessionId: string) => string | null
}

const inert: Startup = { arm: () => {}, take: () => null }
const Ctx = createContext<Startup>(inert)

/** Panes read this. Inert outside a provider, so the cockpit is unaffected. */
export function useStartup(): Startup {
  return useContext(Ctx)
}

/**
 * Creates the store.
 *
 * Separate from the provider because the screen that arms a command is the same
 * one that renders the panes which take it — it cannot read its own context. So
 * it holds the store and hands it down.
 */
export function useStartupStore(): Startup {
  // A ref, not state: arming and taking must not re-render the wall, and the
  // taking happens inside a pane's effect rather than during a render.
  const pending = useRef(new Map<string, string>())

  const arm = useCallback((sessionId: string, command: string) => {
    if (command.trim()) pending.current.set(sessionId, command.trim())
  }, [])

  const take = useCallback((sessionId: string) => {
    const cmd = pending.current.get(sessionId)
    if (cmd === undefined) return null
    // Deleted before it is returned: see the note above about exactly once.
    pending.current.delete(sessionId)
    return cmd
  }, [])

  return useMemo<Startup>(() => ({ arm, take }), [arm, take])
}

export function StartupProvider({ value, children }: { value: Startup; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
